import { api, getApiErrorMessage } from '@/lib/api';
import type { LoginInput, LoginResponse, MeResponse, AuthUser } from './types';

/**
 * Real auth API — talks to /api/v1/auth on the backend.
 *
 * Login is the only auth call that explicitly handles its own 401 — it
 * bypasses the global token-refresh interceptor (see lib/api.ts) because
 * "wrong password" should surface as an error to the user, not silently
 * try to refresh a stale session.
 */
export async function loginRequest(input: LoginInput): Promise<LoginResponse> {
  try {
    const res = await api.post('/auth/login', input);
    const data = (res.data?.data ?? res.data) as LoginResponse;
    return data;
  } catch (err) {
    throw new Error(getApiErrorMessage(err, 'Invalid email or password'));
  }
}

export async function logoutRequest(): Promise<void> {
  try {
    await api.post('/auth/logout', {});
  } catch {
    // Logout is best-effort — even if the server rejects (stale token etc.)
    // we still clear the local session in the hook.
  }
}

export async function meRequest(): Promise<MeResponse> {
  const res = await api.get('/auth/me');
  return (res.data?.data ?? res.data) as MeResponse;
}

/**
 * Legacy export kept for backwards compatibility with DashboardPage and
 * MySettingsPage, which filter chamber members for dropdowns. These lists
 * will be empty until a /users endpoint exists; the pages degrade to an
 * empty dropdown rather than crashing.
 * TODO: replace consumers with a real useQuery against GET /api/v1/users.
 */
export const CHAMBER_MEMBERS: AuthUser[] = [];
